import JSZip from 'jszip'

const DOCUMENT_EXTENSIONS = ['.docx', '.doc', '.pdf', '.html', '.htm'] as const

export interface CctDocumentoExtraido {
  file: File
  fileName: string
  zipFileName: string
  mimeType: string
}

function extensionOf(path: string): string {
  const base = path.split('/').pop()?.split('\\').pop() ?? path
  const i = base.lastIndexOf('.')
  return i >= 0 ? base.slice(i).toLowerCase() : ''
}

function mimeForExtension(ext: string): string {
  switch (ext) {
    case '.pdf':
      return 'application/pdf'
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case '.doc':
      return 'application/msword'
    case '.html':
    case '.htm':
      return 'text/html'
    default:
      return 'application/octet-stream'
  }
}

function isDocumentEntry(path: string): boolean {
  const normalized = path.replace(/\\/g, '/')
  if (normalized.endsWith('/')) return false
  if (normalized.includes('__MACOSX/')) return false
  const base = normalized.split('/').pop() ?? ''
  if (base.startsWith('.')) return false
  return DOCUMENT_EXTENSIONS.includes(extensionOf(base) as (typeof DOCUMENT_EXTENSIONS)[number])
}

function scoreDocument(path: string): number {
  const ext = extensionOf(path)
  // Preferência: docx > pdf > html > doc
  if (ext === '.docx') return 4
  if (ext === '.pdf') return 3
  if (ext === '.html' || ext === '.htm') return 2
  if (ext === '.doc') return 1
  return 0
}

/**
 * Extrai o documento (Word/PDF/HTML) de dentro do zip da convenção.
 * Se houver vários, escolhe pelo tipo preferido e depois pelo nome.
 */
export async function extractDocumentoFromZip(zipFile: File): Promise<CctDocumentoExtraido> {
  const zip = await JSZip.loadAsync(await zipFile.arrayBuffer())

  const candidates = Object.keys(zip.files)
    .filter((path) => {
      const entry = zip.files[path]
      return entry && !entry.dir && isDocumentEntry(path)
    })
    .sort((a, b) => {
      const scoreDiff = scoreDocument(b) - scoreDocument(a)
      if (scoreDiff !== 0) return scoreDiff
      return a.localeCompare(b, 'pt-BR')
    })

  if (candidates.length === 0) {
    throw new Error(
      `Nenhum documento (.docx, .doc, .pdf, .html) encontrado dentro de ${zipFile.name}`,
    )
  }

  const path = candidates[0]
  const entry = zip.files[path]
  const blob = await entry.async('blob')
  const fileName = path.split('/').pop()?.split('\\').pop() || path
  const ext = extensionOf(fileName)
  const mimeType = mimeForExtension(ext)
  const file = new File([blob], fileName, { type: mimeType || blob.type || 'application/octet-stream' })

  return {
    file,
    fileName,
    zipFileName: zipFile.name,
    mimeType: file.type,
  }
}
